import React, { createContext, useContext, useRef, useCallback, ReactNode } from 'react';

/**
 * 标签页缓存数据接口
 * 每个标签页可以存储任意类型的数据
 */
export interface TabCacheData {
  [key: string]: any;
}

/**
 * 标签页缓存项
 */
interface TabCacheItem {
  businessId: string; // 业务ID（如 breakdownID, budgetID 等）
  tabId: string; // 标签页ID
  path: string; // 路径
  data: TabCacheData; // 缓存数据
  timestamp: number; // 缓存时间戳
}

/**
 * TabCacheContext 接口
 */
interface TabCacheContextType {
  /**
   * 获取缓存数据
   */
  getCache: (tabId: string) => TabCacheData | null;
  
  /**
   * 设置缓存数据
   */
  setCache: (tabId: string, businessId: string, path: string, data: TabCacheData) => void;
  
  /**
   * 清除指定标签页的缓存
   */
  clearCache: (tabId: string) => void;
  
  /**
   * 检查缓存是否存在
   */
  hasCache: (tabId: string) => boolean;
  
  /**
   * 根据业务ID获取标签页ID
   */
  getTabIdByBusinessId: (businessId: string) => string | null;
  
  /**
   * 清除所有缓存
   */
  clearAllCache: () => void;
  
  /**
   * 获取所有缓存的标签页ID列表
   */
  getCachedTabIds: () => string[];
}

const TabCacheContext = createContext<TabCacheContextType | undefined>(undefined);

/**
 * TabCacheProvider 组件
 */
export const TabCacheProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // 使用 Map 存储缓存，key 为 tabId
  const cacheRef = useRef<Map<string, TabCacheItem>>(new Map());
  
  // 业务ID到标签页ID的映射，用于确保相同业务ID的标签只存在一个
  const businessIdToTabIdRef = useRef<Map<string, string>>(new Map());

  /**
   * 获取缓存数据
   */
  const getCache = useCallback((tabId: string): TabCacheData | null => {
    const cacheItem = cacheRef.current.get(tabId);
    return cacheItem ? cacheItem.data : null;
  }, []);

  /**
   * 设置缓存数据
   */
  const setCache = useCallback((tabId: string, businessId: string, path: string, data: TabCacheData) => {
    // 如果业务ID已存在且对应不同的标签页ID，清除旧的缓存
    // 但只有在数据有效时才清除旧缓存（避免清除正在使用的有效缓存）
    const existingTabId = businessIdToTabIdRef.current.get(businessId);
    if (existingTabId && existingTabId !== tabId) {
      const existingCache = cacheRef.current.get(existingTabId);
      // 只有当新数据有效时才清除旧缓存
      // 检查新数据是否有效（不是空对象或包含必要字段）
      const isNewDataValid = data && 
        typeof data === 'object' && 
        Object.keys(data).length > 0 &&
        !(data.breakdownInfo === undefined && data.scenes === undefined);
      
      if (isNewDataValid) {
        // 只有当旧缓存存在且新数据有效时，才清除旧缓存
        if (existingCache) {
          console.log(`清除旧缓存: ${existingTabId}，设置新缓存: ${tabId}`, { businessId });
          cacheRef.current.delete(existingTabId);
        }
      } else {
        // 如果新数据无效，不更新映射，保留旧缓存
        console.warn(`新缓存数据无效，保留旧缓存: ${existingTabId}`, { tabId, businessId, data });
        return;
      }
    }
    
    // 设置新的缓存
    cacheRef.current.set(tabId, {
      businessId,
      tabId,
      path,
      data,
      timestamp: Date.now()
    });
    
    // 更新业务ID映射
    businessIdToTabIdRef.current.set(businessId, tabId);
  }, []);

  /**
   * 清除指定标签页的缓存
   */
  const clearCache = useCallback((tabId: string) => {
    const cacheItem = cacheRef.current.get(tabId);
    if (cacheItem) {
      // 清除业务ID映射
      businessIdToTabIdRef.current.delete(cacheItem.businessId);
      // 清除缓存
      cacheRef.current.delete(tabId);
    }
  }, []);

  /**
   * 检查缓存是否存在
   */
  const hasCache = useCallback((tabId: string): boolean => {
    return cacheRef.current.has(tabId);
  }, []);

  /**
   * 根据业务ID获取标签页ID
   */
  const getTabIdByBusinessId = useCallback((businessId: string): string | null => {
    return businessIdToTabIdRef.current.get(businessId) || null;
  }, []);

  /**
   * 清除所有缓存
   */
  const clearAllCache = useCallback(() => {
    cacheRef.current.clear();
    businessIdToTabIdRef.current.clear();
  }, []);

  /**
   * 获取所有缓存的标签页ID列表
   */
  const getCachedTabIds = useCallback((): string[] => {
    return Array.from(cacheRef.current.keys());
  }, []);

  const value: TabCacheContextType = {
    getCache,
    setCache,
    clearCache,
    hasCache,
    getTabIdByBusinessId,
    clearAllCache,
    getCachedTabIds
  };

  return (
    <TabCacheContext.Provider value={value}>
      {children}
    </TabCacheContext.Provider>
  );
};

/**
 * 使用 TabCacheContext 的 Hook
 */
export const useTabCache = (): TabCacheContextType => {
  const context = useContext(TabCacheContext);
  if (context === undefined) {
    throw new Error('useTabCache must be used within a TabCacheProvider');
  }
  return context;
};

/**
 * 从路径或标签ID中提取业务ID的工具函数
 */
export const extractBusinessIdFromTab = (tabId: string, path: string): string | null => {
  // 处理 Breakdown: breakdownList-${breakdownID} 或 /breakdown/${breakdownID}
  if (tabId.startsWith('breakdownList-')) {
    return tabId.replace('breakdownList-', '');
  }
  if (path.startsWith('/breakdown/')) {
    const match = path.match(/^\/breakdown\/([^/]+)/);
    return match ? match[1] : null;
  }
  if (path.startsWith('/elements/')) {
    const match = path.match(/^\/elements\/([^/]+)/);
    return match ? match[1] : null;
  }
  
  // 处理 Budget: budget-${budgetID} 或 /budget/${budgetID}
  if (tabId.startsWith('budget-')) {
    return tabId.replace('budget-', '');
  }
  if (path.startsWith('/budget/')) {
    const match = path.match(/^\/budget\/([^/]+)/);
    return match ? match[1] : null;
  }
  
  // 处理 Schedule: schedule-${scheduleID} 或 /schedule/${scheduleID}
  if (tabId.startsWith('schedule-')) {
    return tabId.replace('schedule-', '');
  }
  if (path.startsWith('/schedule/')) {
    const match = path.match(/^\/schedule\/([^/]+)/);
    return match ? match[1] : null;
  }
  
  // 处理 Film: film-${filmID} 或 /film/${filmID}
  if (tabId.startsWith('film-')) {
    return tabId.replace('film-', '');
  }
  if (path.startsWith('/film/')) {
    const match = path.match(/^\/film\/([^/]+)/);
    return match ? match[1] : null;
  }
  
  // 处理创建预算: create-budget-${budgetID}
  if (tabId.startsWith('create-budget-')) {
    return tabId.replace('create-budget-', '');
  }
  
  // 如果没有匹配到，返回 null（如 home 标签）
  return null;
};

